import { Component, OnInit } from '@angular/core';
import { ConfirmationDialogService } from '../confirmation-dialog/confirmation-dialog.service';
import { HttpService } from '../services/http.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { NotificationNavigationService } from '../services/notification-navigation.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { URLUtils } from '../urlUtils';
import { ensureActiveSubscription } from '../shared/subscription-dialog'; 
import { ConfirmPlanComponent } from 'src/app/confirm-plan/confirm-plan.component';
import { MatDialog } from '@angular/material/dialog';

// ── Pagination config ──────────────────────────────────────────────────────
const PAGE_SIZE = 10;

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    standalone: false
})
export class NotificationsComponent implements OnInit {

  // ── State ────────────────────────────────────────────────────────────────
  notificationsList: any[] = [];       // full list from API
  pagedNotifications: any[] = [];      // current page slice
  selectedNotes: any[] = [];
  postObject: any[] = [];

  // Pagination
  currentPage = 1;
  pageSize    = PAGE_SIZE;
  totalPages  = 1;
  fromCount: number = 0;
  toCount: number = 0;
  
  getPlan:any;
  getCategory:any;
  restrictedPlans = ['free', 'professional', 'starter'];

  constructor(
    private httpService:              HttpService,
    private router:                   Router,
    private confirmationDialogService: ConfirmationDialogService,
    public  toastr:                   ToastrService,
    private spinnerService:           NgxSpinnerService,
    private notificationNavService:   NotificationNavigationService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.getPlan = localStorage.getItem("plan"); // get current plan
    this.getCategory = localStorage.getItem("category"); // get current category

    this.getNotificationList();
  }

  // ── Data loading ─────────────────────────────────────────────────────────

  getNotificationList(): void {
    this.spinnerService.show();
    this.httpService
      .sendGetRequest(URLUtils.getNotifications)
      .subscribe((res: any) => {
        this.spinnerService.hide();

        // Initialise helper flags on every item
        this.notificationsList = (res.data.notifications ?? []).map((n: any) => ({
          ...n,
          isChecked: false,
        }));

        // Sync global unread badge
        const unread = this.notificationsList.filter((n: any) => n.status === 'unread').length;
        this.httpService.updateUnreadCount(unread);

        // Reset selection & re-page
        this.selectedNotes = [];
        this.currentPage   = 1;
        this.updatePagination();
      });
  }

  // ── Pagination ────────────────────────────────────────────────────────────

  // updatePagination(): void {
  //   this.totalPages        = Math.max(1, Math.ceil(this.notificationsList.length / this.pageSize));
  //   this.currentPage       = Math.min(this.currentPage, this.totalPages);
  //   const start            = (this.currentPage - 1) * this.pageSize;
  //   this.pagedNotifications = this.notificationsList.slice(start, start + this.pageSize);
  // }

  updatePagination(): void {
  this.totalPages = Math.max(1, Math.ceil(this.notificationsList.length / this.pageSize));
  this.currentPage = Math.min(this.currentPage, this.totalPages);

  const start = (this.currentPage - 1) * this.pageSize;
  const end = start + this.pageSize;

  this.pagedNotifications = this.notificationsList.slice(start, end);

  this.fromCount = this.notificationsList.length === 0 ? 0 : start + 1;
  this.toCount = Math.min(end, this.notificationsList.length);
}

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  // ── Notification click (navigate + mark read locally) ────────────────────

  onNotificationClick(notification: any): void {
    if (!notification?.navigation) {
      console.warn('No navigation object for notification:', notification.id);
      return;
    }

    // Mark as read locally so badge/dot disappear immediately (no full reload needed)
    if (notification.status === 'unread') {
      this.httpService
        .sendDeleteRequestwithObj(URLUtils.readNotifications, [notification.id])
        .subscribe(() => {
          notification.status = 'read';   // ← local update: dot & bold removed instantly
          this.syncUnreadCount();
        });
    }

    this.notificationNavService.navigate(notification.navigation);
  }

  // ── Checkbox selection ───────────────────────────────────────────────────

  isCheckBox(checked: boolean, note: any): void {
    note.isChecked = checked;
    if (checked) {
      if (!this.selectedNotes.find((n: any) => n.id === note.id)) {
        this.selectedNotes.push(note);
      }
    } else {
      this.selectedNotes = this.selectedNotes.filter((n: any) => n.id !== note.id);
    }
  }

  // ── Bulk actions ─────────────────────────────────────────────────────────

    //subscription check for action button
  checkPlan(event?: MouseEvent): boolean {
    const restrictedPlans = ['free', 'professional', 'starter'];

    if (restrictedPlans.includes(this.getPlan)) {
      this.dialog.open(ConfirmPlanComponent, {
        width: '400px',
        data: { plan: this.getPlan }
      });

      return false;   // Stop further execution
    }
    return true;      // Allow further execution
  }

  readNotification(): void {
    this.collectedIds();
    if (this.postObject.length === 0) {
      this.toastr.error('Select at least 1 item');
      return;
    }
    if (!ensureActiveSubscription(this.confirmationDialogService)) { return; }
    if (!this.checkPlan()) {
      return;
    } // Stop here if plan is restricted

    this.httpService
      .sendDeleteRequestwithObj(URLUtils.readNotifications, this.postObject)
      .subscribe((res: any) => {
        this.toastr.success(res.msg);
        this.resetCheckboxes();
        this.getNotificationList();   // full reload to get server-confirmed state
      });
  }

  deleteNotification(): void {
    this.collectedIds();
    if (this.postObject.length === 0) {
      this.toastr.error('Select at least 1 item');
      return;
    }
    if (!ensureActiveSubscription(this.confirmationDialogService)) { return; }
    if (!this.checkPlan()) {
      return;
    } // Stop here if plan is restricted

    this.httpService
      .sendDeleteRequestwithObj(URLUtils.deleteNotifications, this.postObject)
      .subscribe((res: any) => {
        this.toastr.success(res.msg);
        this.resetCheckboxes();
        this.getNotificationList();
      });
  }

  collectedIds(): void {
    this.postObject = this.selectedNotes.map((n: any) => n.id);
  }

  // Reset every checkbox in the current list and clear selection array
  private resetCheckboxes(): void {
    this.notificationsList.forEach((n: any) => (n.isChecked = false));
    this.selectedNotes = [];
    this.postObject    = [];
  }

  // Recompute & push the global unread count without a network call
  private syncUnreadCount(): void {
    const unread = this.notificationsList.filter((n: any) => n.status === 'unread').length;
    this.httpService.updateUnreadCount(unread);
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  getAvatarInitial(note: any): string {
    if (!note?.message) return '?';
    const words = note.message.trim().split(/\s+/);
    return words[0]?.charAt(0)?.toUpperCase() || '?';
  }

  private avatarColorClasses = [
    'avatar-teal', 'avatar-indigo', 'avatar-orange',
    'avatar-purple', 'avatar-blue', 'avatar-green',
    'avatar-red', 'avatar-gray',
  ];

  getAvatarClass(note: any): string {
    if (!note?.id) return 'avatar-blue';
    const sum = (note.id as string)
      .split('')
      .reduce((acc: number, c: string) => acc + c.charCodeAt(0), 0);
    return this.avatarColorClasses[sum % this.avatarColorClasses.length];
  }

  getDateGroupCount(dateLabel: string): number {
    if (!this.notificationsList) return 0;
    return this.notificationsList.filter((n: any) => {
      const label = new Date(n.timestamp).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      });
      return label === dateLabel;
    }).length;
  }
}